import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Edit2,
  Plus,
  RefreshCw,
  Target,
  Trash2,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { cn } from '@/lib/utils'
import type {
  Recipe,
  RecipeFormData,
  ValidationResult,
} from './hooks/use-recipes'
import { useRecipes } from './hooks/use-recipes'
import { useSubtopics } from './hooks/use-subtopics'
import { RecipeEditorModal } from './recipe-editor-modal'

export function RecipesSection() {
  const {
    recipes,
    loading,
    error,
    refresh,
    createRecipe,
    updateRecipe,
    deleteRecipe,
    validateRecipe,
  } = useRecipes()

  const { subtopics } = useSubtopics()

  const [isCollapsed, setIsCollapsed] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null)
  const [recipeToDelete, setRecipeToDelete] = useState<Recipe | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Filter and sort recipes by search query
  const filteredRecipes = recipes
    .filter((recipe) => {
      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      return (
        recipe.name.toLowerCase().includes(query) ||
        recipe.shortDescription?.toLowerCase().includes(query) ||
        recipe.slug.toLowerCase().includes(query)
      )
    })
    .sort((a, b) => a.name.localeCompare(b.name))

  const handleCreateOrUpdate = async (
    data: RecipeFormData,
  ): Promise<Recipe> => {
    setIsSubmitting(true)
    try {
      let result: Recipe
      if (editingRecipe) {
        result = await updateRecipe(editingRecipe.id, data)
        toast.success('Recipe updated')
      } else {
        result = await createRecipe(data)
        toast.success('Recipe created')
      }
      setShowModal(false)
      setEditingRecipe(null)
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save'
      toast.error(message)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleValidate = async (
    id: string,
    language: string,
    options?: {
      code?: string
      testCode?: string
      testExpectedOutput?: string
    },
  ): Promise<ValidationResult> => {
    return validateRecipe(id, language, options)
  }

  const handleOpenModal = () => {
    setEditingRecipe(null)
    setShowModal(true)
  }

  const handleEdit = (recipe: Recipe) => {
    setEditingRecipe(recipe)
    setShowModal(true)
  }

  const handleDelete = (recipe: Recipe) => {
    setRecipeToDelete(recipe)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!recipeToDelete) return
    setIsSubmitting(true)
    try {
      await deleteRecipe(recipeToDelete.id)
      toast.success('Recipe deleted')
      setShowDeleteConfirm(false)
      setRecipeToDelete(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="mt-8 rounded-lg border border-border bg-card">
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex w-full items-center justify-between p-4 transition-colors hover:bg-muted/50"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 p-2">
              <BookOpen size={20} className="text-white" />
            </div>
            <div className="text-left">
              <h2 className="text-lg font-semibold text-foreground">Recipes</h2>
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          </div>
          {isCollapsed ? (
            <ChevronDown size={20} className="text-muted-foreground" />
          ) : (
            <ChevronUp size={20} className="text-muted-foreground" />
          )}
        </button>
        {!isCollapsed && (
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <RefreshCw size={16} className="animate-spin" />
              Loading recipes...
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="mt-8 rounded-lg border border-border bg-card">
      {/* Collapsible Header */}
      <div className="flex items-center justify-between p-4">
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="-m-2 flex flex-1 items-center gap-3 rounded-lg p-2 transition-colors hover:bg-muted/50"
        >
          <div className="rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 p-2">
            <BookOpen size={20} className="text-white" />
          </div>
          <div className="text-left">
            <h2 className="text-lg font-semibold text-foreground">Recipes</h2>
            <p className="text-sm text-muted-foreground">
              {recipes.length} recipe{recipes.length !== 1 ? 's' : ''}
            </p>
          </div>
          {isCollapsed ? (
            <ChevronDown size={20} className="text-muted-foreground" />
          ) : (
            <ChevronUp size={20} className="text-muted-foreground" />
          )}
        </button>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleOpenModal}>
            <Plus size={16} className="mr-1" />
            Add Recipe
          </Button>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Content - Collapsible */}
      {!isCollapsed && (
        <div className="border-t border-border p-4">
          {error ? (
            <div className="py-8 text-center">
              <p className="mb-2 text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" onClick={refresh}>
                Retry
              </Button>
            </div>
          ) : recipes.length === 0 ? (
            <div className="py-8 text-center">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <BookOpen size={24} className="text-muted-foreground" />
              </div>
              <h3 className="mb-2 font-medium text-foreground">
                No recipes yet
              </h3>
              <p className="mb-4 text-sm text-muted-foreground">
                Create reusable algorithmic patterns with pseudocode and
                implementations.
              </p>
              <Button size="sm" onClick={handleOpenModal}>
                <Plus size={16} className="mr-1" />
                Create Recipe
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Search */}
              <div className="mb-4">
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search recipes..."
                  className="w-full"
                />
              </div>

              {/* Recipe List */}
              <div className="space-y-2">
                {filteredRecipes.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-muted-foreground">No recipes found</p>
                  </div>
                ) : (
                  filteredRecipes.map((recipe) => (
                    <RecipeListItem
                      key={recipe.id}
                      recipe={recipe}
                      onEdit={() => handleEdit(recipe)}
                      onDelete={() => handleDelete(recipe)}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <RecipeEditorModal
        isOpen={showModal}
        recipe={editingRecipe}
        onClose={() => {
          setShowModal(false)
          setEditingRecipe(null)
        }}
        onSave={handleCreateOrUpdate}
        onValidate={handleValidate}
        isSubmitting={isSubmitting}
        allSubtopics={subtopics.map((s) => ({
          id: s.id,
          name: s.name,
          topicName: s.topicName,
        }))}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && recipeToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle size={20} className="text-destructive" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Delete Recipe
                </h2>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <p className="mb-6 text-foreground">
              Are you sure you want to delete{' '}
              <strong>{recipeToDelete.name}</strong>?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setRecipeToDelete(null)
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Separate component for recipe list item
interface RecipeListItemProps {
  recipe: Recipe
  onEdit: () => void
  onDelete: () => void
}

function RecipeListItem({ recipe, onEdit, onDelete }: RecipeListItemProps) {
  const languageSummary = recipe.languageSummary || {}
  const linkedSubtopics = recipe.subtopics || []

  return (
    <div className="group flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted/50">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-foreground">{recipe.name}</h3>
          {/* Linked subtopics badge */}
          {linkedSubtopics.length > 0 && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-0.5 text-xs text-violet-600 dark:text-violet-400"
              title={`Linked to: ${linkedSubtopics.map((s) => s.name).join(', ')}`}
            >
              <Target className="h-3 w-3" />
              {linkedSubtopics.length}
            </span>
          )}
          {/* Language validation indicators */}
          <div className="flex items-center gap-1">
            {languageSummary.python !== undefined && (
              <span
                className="flex items-center gap-0.5 text-xs"
                title={
                  languageSummary.python
                    ? 'Python validated'
                    : 'Python not validated'
                }
              >
                {languageSummary.python ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                <span className="text-muted-foreground">py</span>
              </span>
            )}
            {languageSummary.javascript !== undefined && (
              <span
                className="flex items-center gap-0.5 text-xs"
                title={
                  languageSummary.javascript
                    ? 'JavaScript validated'
                    : 'JavaScript not validated'
                }
              >
                {languageSummary.javascript ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                <span className="text-muted-foreground">js</span>
              </span>
            )}
          </div>
        </div>
        {recipe.shortDescription && (
          <p className="line-clamp-1 text-sm text-muted-foreground">
            {recipe.shortDescription}
          </p>
        )}
        <div className="mt-1 text-xs text-muted-foreground">
          <code>{recipe.slug}</code>
        </div>
      </div>
      <div className={cn(REVEAL_ON_HOVER, 'flex items-center gap-1')}>
        <button
          type="button"
          onClick={onEdit}
          className="rounded p-2 text-muted-foreground transition-all hover:text-foreground"
          title="Edit recipe"
        >
          <Edit2 size={16} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded p-2 text-muted-foreground transition-all hover:text-destructive"
          title="Delete recipe"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  )
}
